import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography,   
    CircularProgress, 
    TextField, 
    Button,  
    MenuItem, 
    Select, 
    FormControl, 
    InputLabel,
    Autocomplete, 
} from '@mui/material';
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { de, enGB } from 'date-fns/locale';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { parseUTCDate } from 'state/helpers';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import CustomDatePicker from "components/CustomDatePicker";


const CrewPerformancePage = () => {
    useAuthCheck();
    const { crewPerformanceId } = useParams();    
    const navigate = useNavigate();
    const locales = { 'en-us': undefined, 'en-gb': enGB, de };
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    const [crewMembers, setCrewMembers] = useState([]); 
    const LEVELS = [
        {
            type: "common", 
            value: ["None"]
        },
        {
            type: "Discipline",
            value: [
                "Lower KPI", 
                "Reminder", 
                "Warning", 
                "Reprimand", 
                "Termination"
            ]
        },
        {
            type: "Reward",
            value: [
                "Achievement",
                "Initiative",
                "Compliance",
                "Safety",
                "Service",
            ]
        },        
    ];
    const getLevelOptions = (performanceType) => {
        const common = LEVELS.find(l => l.type === "common")?.value ?? [];

        if (performanceType === "Discipline") {
            return [...common, ...LEVELS.find(l => l.type === "Discipline").value];
        }

        if (performanceType === "Reward") {
            return [...common, ...LEVELS.find(l => l.type === "Reward").value];
        }

        return common;
    };

    const KPI_OPTIONS = ["A", "B", "C", "0"];

    const [formValues, setFormValues] = useState({
        crewCode: "",
        crewName: "",
        date: new Date(),
        performanceType: "Discipline",
        level: "None",
        description: "",
        kpi: "A",
        points: 0,

        disciplinaryAction: "",
        effectiveTo: null,
        attachments: [],
    });
    
    
    const fetchCrewMembers = useCallback(async () => {
        return await api.get('/crewmembers');
    }, []);

    const fetchCrewPerformance = useCallback(async () => {
        if (!crewPerformanceId) return null;
        return await api.get(
            `/crewperformances/${crewPerformanceId}`
        );
    }, [crewPerformanceId]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const [
                    crewMembersRes, 
                    performanceRes
                ] = await Promise.all([
                    fetchCrewMembers(),
                    crewPerformanceId ? fetchCrewPerformance() : Promise.resolve(null),
                ]);

                setCrewMembers(crewMembersRes.data);

                const data = performanceRes?.data;
                if (data) {
                    setFormValues({
                        crewCode: data.crewCode || "",
                        crewName: data.crewName || "",
                        date: data.date ? new Date(data.date) : new Date(),
                        performanceType: data.performanceType || "Reward",
                        level: data.level || "None",
                        description: data.description || "",
                        points: data.points || 0,

                        disciplinaryAction: data.disciplinaryAction || "",
                        effectiveTo: data.effectiveTo ? new Date(data.effectiveTo) : null,

                        kpi: data.kpi || "A",
                        attachments: data.attachments || [],
                    });
                }
            } catch (err) {
                toast.error("Failed to fetch data!");
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [
        fetchCrewMembers, 
        fetchCrewPerformance, 
        crewPerformanceId
    ]);
    
    const validationSchema = yup.object().shape({
        crewCode: yup.string().required("Crew Code required"),
        date: yup.string().required(),
        performanceType: yup.string().required(),
    });

    const handleFormSubmit = async (values) => {
        try {
            const payload = {
                ...values,
                date: values.date
                    ? parseUTCDate(values.date) 
                    : null,
                effectiveTo: values.effectiveTo
                    ? parseUTCDate(values.effectiveTo) 
                    : null,
            };

            const url = crewPerformanceId
                ? `/crewperformances/update/${crewPerformanceId}`
                : '/crewperformances/new';

            const method = crewPerformanceId ? "put" : "post";

            const res = await api({ method, url, data: payload });

            if (![200, 201].includes(res.status)) {
                toast.error("Failed to submit!");
                return;
            }

            toast.success(crewPerformanceId
                ? "Crew performance updated."
                : "Crew performance created."
            );

            navigate("/crewperformances");
        } catch (err) {
            toast.error(error.message || "Failed to submit!");
        }
    };


    return (
        <Box className="main-container-box">                
            <FlexBetween>
                <Header
                    title="Crew Performance"
                    subtitle={`${loading ? 'Loading...' : crewPerformanceId ? 'Update' : 'Create'}`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <StyledButton 
                            onClick={() => navigate("/crewperformances")}
                        >
                            <ArrowBackIosNewIcon />
                        </StyledButton>
                    </Box>
                )}
            </FlexBetween>
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                    <LocalizationProvider 
                        dateAdapter={AdapterDateFns}
                        adapterLocale={locales['en-gb']}
                    >
                        <Formik
                            initialValues={formValues}
                            enableReinitialize={true}
                            validationSchema={validationSchema}
                            onSubmit={handleFormSubmit}
                        >
                            {({
                                values,
                                errors,
                                touched,
                                handleChange,
                                handleBlur,
                                handleSubmit,
                                setFieldValue,
                            }) => {
                                const levelOptions = getLevelOptions(values.performanceType);
                                
                                return (
                                    <form onSubmit={handleSubmit}>
                                        <Box
                                            display="grid"
                                            gap={2}
                                            gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                            sx={{ '& > div': { gridColumn: isDesktop ? undefined : 'span 12' } }}
                                        >
                                            {/* Crew Member */}
                                            <Autocomplete
                                                sx={{ gridColumn: "span 6" }}
                                                options={crewMembers}
                                                getOptionLabel={(o) => `${o.crewCode} - ${o.crewName}`}
                                                value={ crewMembers.find((c) => c.crewCode === values.crewCode) || null}
                                                onChange={(_, newValue) => {
                                                    if (newValue) {
                                                        setFieldValue("crewCode", newValue.crewCode);
                                                        setFieldValue("crewName", newValue.crewName);
                                                    } else {
                                                        setFieldValue("crewCode", "");
                                                        setFieldValue("crewName", "");
                                                    }
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Crew Member"
                                                        variant="filled"
                                                        required
                                                    />
                                                )}
                                            />

                                            {/* Date */}
                                            <CustomDatePicker
                                                required
                                                label="Date"
                                                name="date"
                                                value={values.date}
                                                onChange={(name, v) => setFieldValue(name, v)}
                                                sx={{ gridColumn: "span 6" }}
                                            />

                                            {/* Perfromance Type */}
                                            <FormControl variant="filled" sx={{ gridColumn: "span 6" }}>
                                                <InputLabel>Performance Type</InputLabel>
                                                <Select
                                                    name="performanceType"
                                                    value={values.performanceType}
                                                    onChange={(e) => {
                                                        handleChange(e);
                                                        setFieldValue("level", "None");
                                                    }}
                                                >
                                                    <MenuItem value="Discipline">Discipline</MenuItem>
                                                    <MenuItem value="Reward">Reward</MenuItem>
                                                </Select>
                                            </FormControl>

                                            {/* Level */}
                                            <FormControl variant="filled" sx={{ gridColumn: "span 6" }}>
                                                <InputLabel>Level</InputLabel>
                                                <Select
                                                    name="level"
                                                    value={values.level}
                                                    onChange={handleChange}
                                                >                                                
                                                    {levelOptions.map((lv) => (
                                                        <MenuItem key={lv} value={lv}>
                                                            {lv}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>

                                            {/* Description */}
                                            <TextField
                                                label="Description"
                                                variant="filled"
                                                name="description"
                                                value={values.description}
                                                onChange={handleChange}
                                                sx={{ gridColumn: "span 6" }}
                                            />
                                            
                                            {/* KPI */}
                                            <FormControl variant="filled" sx={{ gridColumn: "span 6" }}>
                                                <InputLabel>KPI</InputLabel>
                                                <Select
                                                    name="kpi"
                                                    value={values.kpi}
                                                    onChange={handleChange}
                                                >                                                
                                                    {KPI_OPTIONS?.map(option => (
                                                        <MenuItem key={option} value={option}>
                                                            {option}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>

                                            {/* <TextField
                                                label="Points"
                                                variant="filled"
                                                type="number"
                                                name="points"
                                                value={values.points}
                                                onChange={handleChange}
                                                slotProps={{
                                                    input: {
                                                        sx: {
                                                            "& input": { textAlign: "right" },
                                                        },
                                                    },
                                                }}
                                                sx={{ gridColumn: "span 6" }}
                                            /> */}

                                            {/* Discipline fields */}
                                            {values.performanceType === "Discipline" && (
                                                <> 
                                                    <TextField
                                                        label="Disciplinary Action"
                                                        variant="filled"
                                                        name="disciplinaryAction"
                                                        value={values.disciplinaryAction}
                                                        onChange={handleChange}
                                                        sx={{ gridColumn: "span 6" }}
                                                    />

                                                    <CustomDatePicker
                                                        label="Effective To"
                                                        name="effectiveTo"
                                                        value={values.effectiveTo}
                                                        onChange={(name, v) => setFieldValue(name, v)}
                                                        sx={{ gridColumn: "span 6" }}
                                                    />
                                                </>
                                            )}

                                            {/* Submit button */}
                                            <Button
                                                fullWidth
                                                type="submit"
                                                variant="contained"
                                                color="secondary"
                                                sx={{ gridColumn: "span 12", height: 42 }}
                                            >
                                                {crewPerformanceId ? "Update" : "Create"}
                                            </Button>
                                        </Box>
                                    </form>
                                )
                            }}
                        </Formik>
                    </LocalizationProvider>
                </Box>
            )}
        </Box>
    );
};

export default CrewPerformancePage;
